import React, { useState, useEffect } from 'react';
import api from '../../api';

const SPECIALTIES = ['General Maintenance','Plumbing','Electrical','HVAC','Cleaning',
  'Landscaping','Pool / Spa','Pest Control','Painting','Roofing','Appliance','Security','Structural'];

const RATING_LABEL = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' };

const INPUT = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7,
  border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', boxSizing: 'border-box' };

function Stars({ n }) {
  if (!n) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  return (
    <span style={{ fontSize: 13, letterSpacing: 1 }}>
      {'★'.repeat(n).split('').map((s, i) => <span key={i} style={{ color: '#f59e0b' }}>{s}</span>)}
      {'☆'.repeat(5 - n).split('').map((s, i) => <span key={i} style={{ color: '#e2e8f0' }}>{s}</span>)}
    </span>
  );
}

function WorkerModal({ worker, onSave, onClose }) {
  const isEdit = !!worker?.id;
  const [form, setForm] = useState(worker || { name: '', email: '', phone: '', specialties: [], notes: '', internal_rating: '' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  function toggleSpecialty(s) {
    setForm(p => ({
      ...p,
      specialties: p.specialties.includes(s)
        ? p.specialties.filter(x => x !== s)
        : [...p.specialties, s],
    }));
  }

  async function handleSave() {
    if (!form.name) return;
    setSaving(true);
    try {
      if (isEdit) await api.put(`/pm/service-workers/${worker.id}`, form);
      else await api.post('/pm/service-workers', form);
      onSave();
    } catch {} finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#1e293b' }}>
          {isEdit ? 'Edit Service Worker' : 'Add Service Worker'}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Name *</label>
          <input value={form.name} onChange={set('name')} placeholder="Full name" style={INPUT} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Phone</label>
            <input value={form.phone || ''} onChange={set('phone')} placeholder="808-555-0100" style={INPUT} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Email</label>
            <input value={form.email || ''} onChange={set('email')} placeholder="worker@email.com" style={INPUT} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 8 }}>Specialties</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SPECIALTIES.map(s => {
              const on = (form.specialties || []).includes(s);
              return (
                <button key={s} onClick={() => toggleSpecialty(s)}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, border: 'none',
                    cursor: 'pointer', fontWeight: on ? 700 : 400,
                    background: on ? '#0d9488' : '#f1f5f9',
                    color: on ? '#fff' : '#475569' }}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Internal Rating <span style={{ color: '#94a3b8', fontWeight: 400 }}>(not visible to worker)</span>
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => setForm(p => ({ ...p, internal_rating: p.internal_rating === n ? '' : n }))}
                style={{ width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16,
                  background: form.internal_rating === n ? '#fef3c7' : '#f8fafc',
                  color: form.internal_rating === n ? '#b45309' : '#94a3b8' }}>
                ★
              </button>
            ))}
            {form.internal_rating ? <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>{RATING_LABEL[form.internal_rating]}</span> : null}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea value={form.notes || ''} rows={3} onChange={set('notes')}
            placeholder="License #, insurance info, notes…"
            style={{ ...INPUT, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Worker'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SetLoginModal({ worker, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  function generate() {
    const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
    setPw(Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''));
  }

  async function handleSet() {
    if (!pw || pw.length < 6) return;
    setSaving(true);
    try {
      await api.post(`/pm/service-workers/${worker.id}/set-password`, { temp_password: pw });
      setDone(true);
    } catch {} finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 380, padding: 24 }}>
        {done ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 12 }}>✓ Portal Access Set</div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>Share these credentials with <strong>{worker.name}</strong>:</div>
            <div style={{ background: '#f0fdf9', border: '1px solid #99f6e4', borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>Login at: <strong>lanuirealty.paradiseops.ai</strong></div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Email: <strong>{worker.email}</strong></div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Temp password: <strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{pw}</strong></div>
              <div style={{ fontSize: 11, color: '#0d9488', marginTop: 8 }}>They'll be asked to change it on first login.</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 4 }}>Set Portal Login</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>for {worker.name} ({worker.email})</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Temporary Password</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={pw} onChange={e => setPw(e.target.value)} placeholder="Min 6 characters"
                  style={{ ...INPUT, flex: 1 }} />
                <button className="btn btn-ghost btn-sm" onClick={generate} style={{ flexShrink: 0 }}>Generate</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSet} disabled={saving || pw.length < 6}>
                {saving ? 'Setting…' : 'Set Password'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ServiceWorkersView() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [loginFor, setLoginFor] = useState(null);

  function load() {
    api.get('/pm/service-workers').then(r => setWorkers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function remove(w) {
    if (!window.confirm(`Remove ${w.name}? They won't appear in future assignments.`)) return;
    await api.delete(`/pm/service-workers/${w.id}`).catch(() => {});
    load();
  }

  return (
    <>
      <div className="page-header">
        <span className="page-title">Service Workers</span>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Add Worker</button>
      </div>
      <div className="page-body">
        {loading ? <div className="loading">Loading…</div>
          : workers.length === 0 ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>
                No service workers yet. Add contractors, cleaners, and tradespeople here.
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Specialties</th>
                      <th>Rating</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map(w => (
                      <tr key={w.id}>
                        <td style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{w.name}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>
                          {w.phone && <div>{w.phone}</div>}
                          {w.email && <div style={{ color: '#94a3b8' }}>{w.email}</div>}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {(w.specialties || []).length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {w.specialties.map(s => (
                                <span key={s} style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px',
                                  borderRadius: 10, background: '#f0fdfa', color: '#0d9488' }}>{s}</span>
                              ))}
                            </div>
                          ) : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td><Stars n={w.internal_rating} /></td>
                        <td style={{ fontSize: 12, color: '#64748b', maxWidth: 180 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {w.notes || '—'}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button onClick={() => setModal(w)}
                            style={{ fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                            Edit
                          </button>
                          <button onClick={() => setLoginFor(w)}
                            style={{ fontSize: 12, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}>
                            {w.email ? 'Set Login' : '—'}
                          </button>
                          <button onClick={() => remove(w)}
                            style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>

      {modal !== null && (
        <WorkerModal
          worker={Object.keys(modal).length ? modal : null}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)} />
      )}
      {loginFor && (
        <SetLoginModal worker={loginFor} onClose={() => setLoginFor(null)} />
      )}
    </>
  );
}
